import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { ALL_36_CLASSES, ALL_CLASS_GROUPS } from '../types/bruktbilfinansiering'

const GROUPS = ['BILBRANSJE', 'PERSON', 'FIRMA', 'BILUTLEIE', 'LEASING', 'FORSIKRING'] as const
type Group = typeof GROUPS[number]

interface Props {
  selected: string[]
  onChange: (selected: string[]) => void
}

function sellerClasses(group: Group): string[] {
  return (ALL_CLASS_GROUPS[group] ?? []) as string[]
}

function buyerClasses(group: Group): string[] {
  return (ALL_36_CLASSES as readonly string[]).filter(c => c.endsWith(`_TO_${group}`))
}

function summaryLabel(selected: string[]): string {
  if (selected.length === 0 || selected.length === ALL_36_CLASSES.length) return i18n.t('components:klasseFilter.all35')
  for (const g of GROUPS) {
    const sc = sellerClasses(g)
    if (sc.length > 0 && sc.every(c => selected.includes(c)) && selected.length === sc.length) {
      return g
    }
  }
  return i18n.t('components:klasseFilter.nClasses', { n: selected.length })
}

export default function KlasseFilterPopover({ selected, onChange }: Props) {
  const { t } = useTranslation('components')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function toggleClass(cls: string) {
    if (selected.includes(cls)) onChange(selected.filter(k => k !== cls))
    else onChange([...selected, cls])
  }

  function selectGroup(classes: string[]) {
    const allSelected = classes.every(c => selected.includes(c))
    if (allSelected) {
      onChange(selected.filter(c => !classes.includes(c)))
    } else {
      const next = new Set(selected)
      classes.forEach(c => next.add(c))
      onChange([...next])
    }
  }

  const label = summaryLabel(selected)
  const hasSelection = selected.length > 0 && selected.length < ALL_36_CLASSES.length

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border transition-colors ${
          hasSelection
            ? 'border-primary-500 text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800'
        } hover:border-primary-400`}
      >
        <span>{label}</span>
        <svg className="w-3 h-3 opacity-60" viewBox="0 0 12 8" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M1 1l5 5 5-5" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 min-w-[640px]">
          {/* Quick-select row: global */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <button
              onClick={() => onChange([...ALL_36_CLASSES])}
              className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {t('klasseFilter.all35')}
            </button>
            <button
              onClick={() => onChange([])}
              className="text-xs px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {t('klasseFilter.clearAll')}
            </button>
          </div>

          {/* Quick-select: Selger row */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs text-gray-500 dark:text-gray-400 w-12">{t('klasseFilter.seller')}</span>
            {GROUPS.map(g => (
              <button
                key={g}
                onClick={() => selectGroup(sellerClasses(g))}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  sellerClasses(g).every(c => selected.includes(c))
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          {/* Quick-select: Kjøper row */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-xs text-gray-500 dark:text-gray-400 w-12">{t('klasseFilter.buyer')}</span>
            {GROUPS.map(g => (
              <button
                key={g}
                onClick={() => selectGroup(buyerClasses(g))}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  buyerClasses(g).length > 0 && buyerClasses(g).every(c => selected.includes(c))
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                    : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          {/* Full 35-class checkbox grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 border-t border-gray-100 dark:border-gray-700 pt-3">
            {Object.entries(ALL_CLASS_GROUPS).map(([group, classes]) => (
              <div key={group}>
                <button
                  onClick={() => selectGroup(classes as string[])}
                  className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1 hover:underline"
                >
                  {group}
                </button>
                {(classes as string[]).map(cls => (
                  <label key={cls} className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selected.includes(cls)}
                      onChange={() => toggleClass(cls)}
                      className="accent-primary-600"
                    />
                    <span className="text-xs text-gray-700 dark:text-gray-300">
                      {cls.replace(`${group}_TO_`, '')}
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
